import { normalizeOptionLabel } from '@sportninja/common/utils/optionLabel'
import { generateId, Option } from './types'

/**
 * A dropdown option's label is also its stored answer, and the API column behind
 * it (form_item_values.value_string) is varchar(191) with MySQL strict mode off
 * — a longer label would be silently truncated on write and could then never
 * match its own option again. Mirrored server-side by FormItemRequest.
 */
export const MAX_OPTION_LABEL_LENGTH = 191

/**
 * Product guardrail on how many options one question may carry. NOT a stack
 * limit — nothing here breaks at 501. Mirrored server-side by FormItemRequest.
 */
export const MAX_OPTIONS = 500

export interface ParsedOptionList {
  /**
   * EVERY non-blank line, normalized and free of exact duplicates. Never
   * truncated: over-cap and over-length entries are reported below and kept
   * here, because silently dropping the 501st line would save less than the
   * user pasted with nothing on screen to explain it. Blocking the save is
   * isQuestionFormValid's job.
   */
  options: Option[]
  /** Exact duplicates that were removed, in the order they were dropped. */
  duplicatesRemoved: string[]
  blankLinesIgnored: number
  /** Labels over MAX_OPTION_LABEL_LENGTH — retained in `options`, reported here. */
  tooLong: string[]
  /** Labels differing from an earlier one only by case — retained, warned about. */
  nearDuplicates: string[]
}

/** Renders an option list back into one-label-per-line text. */
export const optionsToText = (options: Option[]): string =>
  options.map((option) => option.label).join('\n')

/**
 * Normalizes one pasted line:
 *   - a tab means a multi-column spreadsheet paste; keep the first column only
 *   - trim, strip ONE wrapping pair of double quotes (CSV quoting)
 *   - canonicalize with normalizeOptionLabel, which trims again and collapses
 *     repeated inner whitespace — the same canonical form the save path uses,
 *     so a paste and a hand-typed row cannot disagree about what counts as a
 *     duplicate
 */
const normalizeLine = (line: string): string => {
  const tabIndex = line.indexOf('\t')
  const firstColumn = tabIndex === -1 ? line : line.slice(0, tabIndex)
  const trimmed = firstColumn.trim()

  const unquoted =
    trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')
      ? trimmed.slice(1, -1)
      : trimmed

  return normalizeOptionLabel(unquoted)
}

/**
 * Parses pasted text into an option list.
 *
 * Exact-duplicate removal is MANDATORY rather than advisory: after a reload an
 * option's client-side id is its label, so two identical labels make the row
 * editor edit both rows at once, delete both at once, and emit duplicate React
 * keys.
 *
 * @param text - Raw textarea contents
 * @returns The parsed options plus everything worth telling the user about
 */
export const parseOptionText = (text: string): ParsedOptionList => {
  const options: Option[] = []
  const duplicatesRemoved: string[] = []
  const tooLong: string[] = []
  const nearDuplicates: string[] = []
  let blankLinesIgnored = 0

  const seenExact = new Set<string>()
  const seenLower = new Set<string>()

  // One generator call per parse; the positional suffix guarantees uniqueness
  // within the batch, which Date.now()+Math.random() alone cannot for 100+
  // options created in the same millisecond.
  const batchId = generateId()

  // Trailing empty segments are the newline the user's paste ended with, not
  // junk they need telling about — an empty textarea must not report "1 blank
  // line ignored". Leading and interior blanks still count.
  const lines = text.split(/\r\n|\n/)
  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop()
  }

  lines.forEach((rawLine) => {
    const label = normalizeLine(rawLine)

    if (!label) {
      blankLinesIgnored += 1
      return
    }

    if (seenExact.has(label)) {
      duplicatesRemoved.push(label)
      return
    }
    seenExact.add(label)

    if (label.length > MAX_OPTION_LABEL_LENGTH) {
      tooLong.push(label)
    }

    const lower = label.toLowerCase()
    if (seenLower.has(lower)) {
      nearDuplicates.push(label)
    }
    seenLower.add(lower)

    options.push({ id: `${batchId}_${options.length}`, label })
  })

  return {
    options,
    duplicatesRemoved,
    blankLinesIgnored,
    tooLong,
    nearDuplicates,
  }
}
